import classNames from 'classnames';
import React from 'react';

import {BackendSourceOptions, discoveryTracker} from '@udemy/browse-event-tracking';
import {ClickEvent, Tracker} from '@udemy/event-tracking';
import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import TrendingIcon from '@udemy/icons/dist/trending-graph.ud-icon';
import {Button, ButtonProps} from '@udemy/react-core-components';

import {TopSkill} from './top-skill.react-component';
import styles from './trending-skills.module.less';
import {TrendingTopics} from './trending-topics.react-component';
import {CategoryWithTopics, Topic, TrackingContext} from './types';
import {URLS, useTrendingTopicsByCategories} from './use-trending-topics-by-categories';

const AllSkillsButton = ({
    onClick,
    isIndiaTopSkillExperimentActive = false,
    isJapanTopSkillExperimentActive = false,
}: Pick<ButtonProps, 'onClick'> & {
    isIndiaTopSkillExperimentActive?: boolean;
    isJapanTopSkillExperimentActive?: boolean;
}) => {
    const {gettext} = useI18n();
    const isExperimentActive = isIndiaTopSkillExperimentActive || isJapanTopSkillExperimentActive;
    return (
        <Button
            componentClass="a"
            href={URLS.FEATURED_TOPICS}
            udStyle={'secondary'}
            onClick={onClick}
        >
            {isExperimentActive
                ? gettext('Show all trending topics')
                : gettext('Show all trending skills')}
            <TrendingIcon label={false} />
        </Button>
    );
};
interface TrendingSkillsProps {
    className?: string;
    columnContentClassName?: string;
    topicCountPerCategory?: number;
    isIndiaTopSkillExperimentActive?: boolean;
    isJapanTopSkillExperimentActive?: boolean;
}

export const TrendingSkills = ({
    className = '',
    columnContentClassName = '',
    topicCountPerCategory = 3,
    isIndiaTopSkillExperimentActive = false,
    isJapanTopSkillExperimentActive = false,
}: TrendingSkillsProps) => {
    const isMobile = useMatchMedia('sm-max');
    const {gettext} = useI18n();
    const {topSkillTopic, trendingTopicsByCategory, isLoading} = useTrendingTopicsByCategories(
        topicCountPerCategory,
        {
            isIndiaTopSkillExperimentActive,
            isJapanTopSkillExperimentActive,
        },
    );

    const trackAllSkillsCTAClick = () => {
        Tracker.publishEvent(
            new ClickEvent({
                componentName: 'showAllTrendingSkills',
            }),
        );
    };

    const getTrackingContext = (): TrackingContext => {
        return {
            trackImpressionFunc: (topic: Topic, position?: number) =>
                discoveryTracker.trackDiscoveryImpression(
                    {
                        item: {
                            ...topic,
                            _class: 'course_topic',
                            type: 'course_topic',
                            tracking_id: String(topic?.id),
                        },
                    },
                    {
                        backendSource: 'discovery' as BackendSourceOptions,
                        index: position,
                    },
                ),
            trackItemClickFunc: (topic: Topic, position?: number) =>
                discoveryTracker.trackDiscoveryItemClickEvent(
                    {
                        item: {
                            ...topic,
                            _class: 'course_topic',
                            type: 'course_topic',
                            tracking_id: String(topic?.id),
                        },
                    },
                    {
                        backendSource: 'discovery' as BackendSourceOptions,
                        index: position,
                    },
                ),
        };
    };

    const filterTopSkillTopicFromTrendingTopics = React.useCallback(
        (categoryTopics: CategoryWithTopics[]) => {
            return categoryTopics.map((category) => {
                return {
                    ...category,
                    topics: category.topics.filter((topic) => topic.id !== topSkillTopic?.id),
                };
            });
        },
        [topSkillTopic],
    );

    const filteredTrendingTopicsByCategory =
        filterTopSkillTopicFromTrendingTopics(trendingTopicsByCategory);

    const getHeading = () => {
        if (isJapanTopSkillExperimentActive) {
            return gettext('Trending topics in Japan');
        }
        if (isIndiaTopSkillExperimentActive) {
            return gettext('Trending topics in India');
        }
        return gettext('Popular Skills');
    };

    const TrendingTopicsComponent = (
        <TrendingTopics
            loading={isLoading}
            categoryWithTopics={filteredTrendingTopicsByCategory}
            topicCountToRender={topicCountPerCategory}
            loadingCategoryCountToRender={3}
            trackingContext={getTrackingContext()}
            isIndiaTopSkillExperimentActive={isIndiaTopSkillExperimentActive}
            isJapanTopSkillExperimentActive={isJapanTopSkillExperimentActive}
        />
    );

    return (
        <div
            className={classNames(
                styles['trending-skills'],
                isJapanTopSkillExperimentActive && styles['japan-variant'],
                className,
            )}
        >
            <div className={styles['header-with-divider']}>
                <h2 className="ud-heading-xxl">{getHeading()}</h2>
            </div>
            <div className={styles['content']}>
                <div className={styles['top-skill-with-cta']}>
                    <TopSkill
                        loading={isLoading || !topSkillTopic}
                        topic={topSkillTopic as Topic}
                        trackingContent={getTrackingContext()}
                        isIndiaTopSkillExperimentActive={isIndiaTopSkillExperimentActive}
                        isJapanTopSkillExperimentActive={isJapanTopSkillExperimentActive}
                    />
                    {!isMobile && (
                        <AllSkillsButton
                            isIndiaTopSkillExperimentActive={isIndiaTopSkillExperimentActive}
                            isJapanTopSkillExperimentActive={isJapanTopSkillExperimentActive}
                            onClick={trackAllSkillsCTAClick}
                        />
                    )}
                </div>

                {isMobile ? (
                    <div
                        className={classNames(
                            styles['wrapped-trending-topics'],
                            columnContentClassName,
                        )}
                    >
                        {TrendingTopicsComponent}
                    </div>
                ) : (
                    TrendingTopicsComponent
                )}
            </div>
            {isMobile && (
                <AllSkillsButton
                    isIndiaTopSkillExperimentActive={isIndiaTopSkillExperimentActive}
                    isJapanTopSkillExperimentActive={isJapanTopSkillExperimentActive}
                    onClick={trackAllSkillsCTAClick}
                />
            )}
        </div>
    );
};
